import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Environment = 'development' | 'production';

interface EnvironmentContextType {
  environment: Environment;
  setEnvironment: (env: Environment) => void;
  isDevelopment: boolean;
  isProduction: boolean;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

interface EnvironmentProviderProps {
  children: ReactNode;
}

export const EnvironmentProvider: React.FC<EnvironmentProviderProps> = ({ children }) => {
  // Initialize from localStorage or URL parameters, defaulting to production
  const [environment, setEnvironmentState] = useState<Environment>(() => {
    // Check URL first for backward compatibility
    if (window.location.href.includes('devMode=true')) {
      return 'development';
    }

    // Check localStorage for backward compatibility
    if (localStorage.getItem('devmode') === 'true') {
      return 'development';
    }

    // Check new localStorage key
    const savedEnv = localStorage.getItem('leelalife-environment') as Environment;
    return savedEnv || 'production';
  });

  const setEnvironment = (env: Environment) => {
    setEnvironmentState(env);
    // Save to localStorage
    localStorage.setItem('leelalife-environment', env);

    // For backward compatibility, also update the old localStorage key
    localStorage.setItem('devmode', env === 'development' ? 'true' : 'false');
  };

  // Sync with localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'leelalife-environment' && e.newValue) {
        setEnvironmentState(e.newValue as Environment);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value: EnvironmentContextType = {
    environment,
    setEnvironment,
    isDevelopment: environment === 'development',
    isProduction: environment === 'production',
  };

  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>;
};

export const useEnvironment = (): EnvironmentContextType => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};
